import { useState } from "react";
import { useTranslations } from "next-intl";
import { usePathname, useSearchParams } from "next/navigation";
import useSWR from "swr";
import { motion } from "framer-motion";
import {
  ExclamationTriangleIcon,
  FaceFrownIcon,
} from "@heroicons/react/24/solid";
import {
  BasketSummary,
  EventData,
  EventShowing,
  EventShowingsMsgs,
} from "@types";
import { basketSummaryFetcher, fetcher, handle429s } from "@utils/fetchers";
import { getCookieValue, joinClassNames } from "@utils/helpers";
import { useIsShopsite } from "@context/DomainContext";
import { Heading, Link, Text, Button } from "@components/theming";
import { Accordion } from "@components/accordion";
import SkeletonLoading from "../components/skeletonLoading";
import ShowingAccordionItem from "../components/showingAccordionItem";
import { EventConfigOverrides } from "@utils/flags";
import { useLatestCookieValue } from "@utils/hooks";
import LoadingSpinner from "@components/loadingSpinner";
import useDomainBasedTranslations from "@utils/useDomainBasedTranslations";

interface Props {
  getPriceRange: ({
    pricesectionIds,
    groupedPricesectionIds,
  }: {
    pricesectionIds?: number[];
    groupedPricesectionIds?: number[];
  }) => { min: number; max: number; isEmpty?: boolean };
  event: EventData;
  eventConfigOverrides?: EventConfigOverrides;
}

const validateShowings = (
  showings?: EventShowingsMsgs["showings"],
  basket?: BasketSummary,
  eventConfigOverrides?: EventConfigOverrides,
  excludeShowingsWithoutDependencies = false
) => {
  if (!showings) return [];
  return showings.filter((showing) => {
    const dependencyRules =
      eventConfigOverrides?.cartValidationRules?.showingDependencies?.filter(
        (dependency) => dependency.dependentShowing === showing.id
      );
    if (dependencyRules && dependencyRules.length > 0) {
      if (!basket || !basket.Groups || basket.Groups.length === 0) {
        return false;
      }
      return dependencyRules.some((rule) => {
        if (rule.requirement.type === "at_least_one") {
          return basket.Groups.some(
            (group) =>
              group.ShowingId &&
              rule.requirement.showings.includes(group.ShowingId)
          );
        }
      });
    }
    if (excludeShowingsWithoutDependencies) {
      return false;
    }
    return true;
  });
};

const EventListMode = ({
  getPriceRange,
  event,
  eventConfigOverrides,
}: Props) => {
  const t = useDomainBasedTranslations("EventPage");
  const utilTranslations = useTranslations("Utils");
  const params = useSearchParams();
  const isShopsite = useIsShopsite();
  const promo = params.get("promo");
  const basketKey = useLatestCookieValue("ASP.NET_SessionId");
  const eventId = event.legacy.e_id;
  const eventVenueId = event.legacy.ev_id;
  const hideNonComboShowings =
    eventConfigOverrides?.hideNonComboShowings === true;

  const pathname = usePathname();
  const draftCookie = getCookieValue("eventkey");
  const draftKey = pathname
    .split("/")
    .find((val) => val.startsWith("key-"))
    ?.replace("key-", "");

  const { data: basket } = useSWR<BasketSummary>(
    basketKey ? `basket/${basketKey}/summary` : null,
    basketSummaryFetcher,
    {
      onErrorRetry: handle429s,
    }
  );

  const {
    data: showingData,
    isLoading,
    mutate,
    error,
  } = useSWR<EventShowingsMsgs>(
    event.status === "published" || (draftCookie && draftKey === draftCookie)
      ? `events/${eventId}/${eventVenueId}/showings${
          promo ? `?promoCode=${promo}` : ""
        }`
      : null,
    fetcher
  );

  const activeShowings = showingData?.showings
    ?.filter((showing) => showing.active)
    .sort((a, b) => a.order - b.order);

  const comboShowings: EventShowing[] = [];
  const nonComboShowings: EventShowing[] = [];
  if (activeShowings) {
    activeShowings.forEach((showing) => {
      if (
        showing.config?.showingcomboconfig?.connectedShowings &&
        showing.config.showingcomboconfig.connectedShowings.length > 0 &&
        showing.config.showingcomboconfig.connectedShowings.length >=
          showing.config.showingcomboconfig.minShowings
      ) {
        comboShowings.push(showing);
      } else {
        nonComboShowings.push(showing);
      }
    });
  }

  const validComboShowings = activeShowings
    ? validateShowings(comboShowings, basket, eventConfigOverrides, false)
    : [];

  // hideNonComboShowings overrides showing.active for non-combos
  // but non-combo showings that are revealed by cart content rules override hideNonComboShowings
  const validNonComboShowings = activeShowings
    ? validateShowings(
        nonComboShowings,
        basket,
        eventConfigOverrides,
        hideNonComboShowings
      )
    : [];

  const [selectedComboShowingId, setSelectedComboShowingId] = useState<
    number | null
  >(validComboShowings.length === 1 ? validComboShowings[0].id : null);

  return !showingData && error && error.request?.status === 429 ? (
    <div
      className={joinClassNames(
        "relative overflow-hidden",
        isShopsite ? "mt-4 sm:mt-8 border border-gray-200 rounded-lg" : "mt-8"
      )}
    >
      <div className="flex flex-col gap-4 items-center justify-center w-full py-8">
        <div className="relative mx-4">
          <Heading
            as="h3"
            size="5"
            weight="medium"
            align="center"
            sharedClasses="relative z-10"
            shopsiteClasses="font-medium text-xl text-center"
          >
            {utilTranslations(
              isShopsite ? "429s.looksbusy" : "429s.looksbusywl"
            )}
          </Heading>
        </div>
        <LoadingSpinner
          size="32"
          color={!isShopsite ? "var(--gray-11)" : undefined}
        />
      </div>
    </div>
  ) : !showingData && error && error.request?.status !== 404 ? (
    <div
      className={joinClassNames(
        "relative overflow-hidden",
        isShopsite ? "mt-4 sm:mt-8 border border-gray-200 rounded-lg" : "mt-8"
      )}
    >
      <div className="flex flex-col items-center justify-center w-full py-8">
        <div className="relative mx-4">
          <Heading
            as="h3"
            size="6"
            weight="bold"
            align="center"
            sharedClasses="relative z-10"
            shopsiteClasses="font-bold text-2xl text-center"
          >
            {utilTranslations("anerroroccurred")}
          </Heading>
        </div>
        <div className="relative text-center mb-4">
          <Text
            as="p"
            size="4"
            color="gray"
            className="relative z-10"
            shopsiteClasses="text-gray-500 text-lg"
          >
            {utilTranslations("dontworrytryagain")}
          </Text>
          <Text
            as="p"
            size="4"
            color="gray"
            className="relative z-10"
            shopsiteClasses="text-gray-500 text-lg"
          >
            {utilTranslations.rich("otherwisecontactsupport", {
              button: (chunks) => (
                <Link
                  href={"/contact"}
                  weight="medium"
                  sharedClasses="underline"
                  shopsiteClasses="text-black font-semibold"
                >
                  {chunks}
                </Link>
              ),
            })}
          </Text>
        </div>
        <Button onClick={() => window?.location.reload()}>
          {utilTranslations("tryagain")}
        </Button>
      </div>
    </div>
  ) : activeShowings && activeShowings.length > 0 ? (
    <div
      className={joinClassNames(
        "flex flex-col gap-5",
        isShopsite && event.subdescription ? "mt-10" : "mt-4"
      )}
    >
      {validComboShowings.length > 0 ? (
        <div className="flex flex-col gap-5">
          {validComboShowings.length > 1 ? (
            <Heading
              as="h2"
              size="4"
              weight="bold"
              shopsiteClasses="text-lg font-semibold"
            >
              {t("choosecombo")}
            </Heading>
          ) : (
            <></>
          )}
          <Accordion
            key="comboshowings"
            type="multiple"
            value={
              validComboShowings.length === 1
                ? [validComboShowings[0].id.toString()]
                : selectedComboShowingId
                ? [selectedComboShowingId.toString()]
                : []
            }
            onValueChange={(v) => {
              if (v.length === 0) {
                setSelectedComboShowingId(null);
              } else {
                setSelectedComboShowingId(parseInt(v[v.length - 1]));
              }
            }}
            disabled={validComboShowings.length === 1}
            asChild
            sharedClasses="overflow-hidden sm:border"
            shopsiteClasses={joinClassNames(
              "bg-white max-sm:-mx-4 sm:rounded-xl",
              validComboShowings.length === 1 ? "-mb-4" : ""
            )}
            whitelabelClasses="border-[color:var(--gray-5)] max-sm:-mx-6 sm:rounded-[var(--radius-4)]"
          >
            <motion.div
              data-testid="comboshowinglistings"
              initial="hidden"
              animate="show"
              exit="hidden"
              variants={{
                hidden: {
                  opacity: 0,
                },
                show: {
                  opacity: 1,
                  transition: {
                    staggerChildren: 0.05,
                  },
                },
              }}
            >
              {validComboShowings.map((showing, index) => (
                <ShowingAccordionItem
                  key={showing.id}
                  activeShowings={activeShowings}
                  index={index}
                  numShowings={validComboShowings.length}
                  showing={showing}
                  messages={showingData?.messages ?? []}
                  event={event}
                  mutate={mutate}
                  getPriceRange={getPriceRange}
                  isCombo
                  onAddToCart={() => {
                    setSelectedComboShowingId(null);
                  }}
                  eventConfigOverrides={eventConfigOverrides}
                />
              ))}
            </motion.div>
          </Accordion>
        </div>
      ) : (
        <></>
      )}
      {validNonComboShowings.length > 0 ? (
        <div
          className={joinClassNames(
            "flex flex-col gap-5",
            validComboShowings.length > 0 ? "mt-4" : ""
          )}
        >
          {validNonComboShowings.length > 1 ? (
            <Heading
              as="h2"
              size="4"
              weight="bold"
              shopsiteClasses="text-lg font-semibold"
            >
              {t("choosedate")}
            </Heading>
          ) : (
            <></>
          )}
          <Accordion
            key="noncomboshowings"
            type="single"
            defaultValue={
              validNonComboShowings.length === 1
                ? validNonComboShowings[0].id.toString()
                : undefined
            }
            disabled={validNonComboShowings.length === 1}
            collapsible={validNonComboShowings.length > 1}
            asChild
            sharedClasses="overflow-hidden sm:border"
            shopsiteClasses={joinClassNames(
              "bg-white max-sm:-mx-4 sm:rounded-xl",
              validNonComboShowings.length === 1 ? "-mb-4" : ""
            )}
            whitelabelClasses="border-[color:var(--gray-5)] max-sm:-mx-6 sm:rounded-[var(--radius-4)]"
          >
            <motion.div
              data-testid="noncomboshowinglistings"
              initial="hidden"
              animate="show"
              exit="hidden"
              variants={{
                hidden: {
                  opacity: 0,
                },
                show: {
                  opacity: 1,
                  transition: {
                    staggerChildren: 0.05,
                  },
                },
              }}
            >
              {validNonComboShowings.map((showing, index) => (
                <ShowingAccordionItem
                  key={showing.id}
                  activeShowings={activeShowings}
                  index={index}
                  numShowings={validNonComboShowings.length}
                  showing={showing}
                  messages={showingData?.messages ?? []}
                  event={event}
                  mutate={mutate}
                  getPriceRange={getPriceRange}
                  eventConfigOverrides={eventConfigOverrides}
                />
              ))}
            </motion.div>
          </Accordion>
        </div>
      ) : (
        <></>
      )}
    </div>
  ) : isLoading ? (
    <SkeletonLoading />
  ) : activeShowings && activeShowings.length === 0 ? (
    <div
      className={joinClassNames(
        "relative overflow-hidden",
        isShopsite ? "mt-4 sm:mt-8 border border-gray-200 rounded-lg" : "mt-8"
      )}
    >
      <div className="flex flex-col items-center justify-center w-full h-[197px]">
        <div className="relative">
          <Heading
            as="h2"
            size="8"
            weight="bold"
            sharedClasses="relative z-10"
            shopsiteClasses="font-black text-4xl"
          >
            {t("error.ohno")}
          </Heading>
          <ExclamationTriangleIcon
            className={joinClassNames(
              "absolute -top-16 -left-16 w-24 h-24 -rotate-12",
              isShopsite ? "text-red-400" : "text-[color:var(--red-a6)]"
            )}
          />
        </div>
        <div className="relative mx-4">
          <Heading
            as="h3"
            size="6"
            weight="bold"
            align="center"
            sharedClasses="relative z-10"
            shopsiteClasses="font-bold text-2xl text-center"
          >
            {t("error.noticketsmessage")}
          </Heading>
          <FaceFrownIcon
            className={joinClassNames(
              "absolute -bottom-12 -right-8 sm:-right-[4.25rem] w-24 h-24 rotate-[15deg]",
              isShopsite ? "text-[#F9CF47]" : "text-[color:var(--yellow-a6)]"
            )}
          />
        </div>
        <Text
          as="p"
          size="4"
          color="gray"
          shopsiteClasses="text-gray-500 text-lg"
        >
          {t("error.tryagain")}
        </Text>
      </div>
    </div>
  ) : (
    <div
      className={joinClassNames(
        "relative overflow-hidden",
        isShopsite ? "mt-4 sm:mt-8 border border-gray-200 rounded-lg" : "mt-8"
      )}
    >
      <div className="flex flex-col items-center justify-center w-full h-[197px]">
        <div className="relative mx-4">
          <Heading
            as="h3"
            size="6"
            weight="bold"
            sharedClasses="text-center"
            shopsiteClasses="font-bold text-2xl"
          >
            {t("error.nodatesmessage")}
          </Heading>
        </div>
        <Text
          as="p"
          color="gray"
          sharedClasses="text-lg"
          shopsiteClasses="text-gray-500"
        >
          {t("error.tryagain")}
        </Text>
      </div>
    </div>
  );
};

export default EventListMode;
